import { useState, useEffect } from 'react';
import Button from '@/components/elements/Button';
import Modal from '@/components/elements/Modal';
import { cookieService } from '@/services/CookieService';
import { useUser } from '@/state/UserContext';

interface CookieConsentProps {
  consentOpen?: boolean;
  showCloseButton?: boolean;
  onClose?: () => void;
}

function CookieConsent({ consentOpen, showCloseButton, onClose }: CookieConsentProps) {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const { geoLocation, isUserLoading } = useUser();

  useEffect(() => {
    if (isUserLoading) return;
    //  Auto-accept cookies for users from non-GDPR countries
    cookieService.handleUserLocation(geoLocation?.country || '');

    // Only show modal for users in a GDPR country who have not yet given consent
    const consent = cookieService.hasConsent();
    const isGdpr = cookieService.isGdprCountry(geoLocation?.country || '');

    if (consent === null && isGdpr) {
      setIsVisible(true);
    }
  }, [isUserLoading]);

  function handleAcceptAll() {
    cookieService.accept();
    setIsVisible(false);
    if (onClose) onClose();
  }

  function handleDecline() {
    cookieService.decline();
    setIsVisible(false);
    if (onClose) onClose();
  }

  return (
    <Modal
      onClose={showCloseButton ? onClose : undefined}
      data-cy='cookie-consent'
      title='We Value Your Privacy'
      isOpen={consentOpen ? consentOpen : isVisible}
      buttons={[
        <Button
          key='essential-only'
          variant='hollow'
          buttonText='Essential Only'
          onClick={handleDecline}
          className='min-w-[100px]'
          data-cy='cookie-consent-essential-only'
        />,
        <Button
          key='accept-all'
          variant='gradient'
          buttonText='Accept All'
          onClick={handleAcceptAll}
          className='min-w-[100px]'
          data-cy='cookie-consent-accept'
        />,
      ]}
    >
      <p className='mt-2 leading-tight'>
        This website uses cookies to enhance your experience and analyse site usage. By clicking &quot;Accept All&quot;,
        you consent to our use of cookies. View our{' '}
        <a
          className='text-[#7AD817] underline'
          href='https://eklipX.io/privacy-policy/'
          target='_blank'
          rel='noreferrer'
        >
          Privacy Policy
        </a>{' '}
        for more information.
      </p>
    </Modal>
  );
}

export default CookieConsent;
